import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import VisitorQRView from './VisitorQRView';
import api from '../api/axiosPublico';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  card: {
    maxWidth: '500px',
    width: '100%',
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-2xl)',
    overflow: 'hidden',
  },
  header: {
    background: 'var(--gradient-premium)',
    padding: '2rem',
    textAlign: 'center',
    color: 'white',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-2xl)',
    fontWeight: '700',
    letterSpacing: 'var(--tracking-tight)',
  },
  subtitle: {
    fontSize: 'var(--text-sm)',
    opacity: 0.9,
    marginTop: '0.5rem',
  },
  content: {
    padding: '2rem',
  },
  qrContainer: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  qrImage: {
    maxWidth: '300px',
    width: '100%',
    height: 'auto',
    margin: '0 auto',
    borderRadius: 'var(--radius-lg)',
    border: '4px solid var(--gold)',
    padding: '1rem',
    backgroundColor: 'white',
  },
  info: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
    color: 'var(--text)',
  },
  infoIcon: {
    color: 'var(--gold)',
    flexShrink: 0,
  },
  infoLabel: {
    fontSize: 'var(--text-sm)',
    color: 'var(--text-secondary)',
    marginBottom: '0.25rem',
  },
  infoValue: {
    fontSize: 'var(--text-base)',
    fontWeight: '600',
    color: 'var(--text)',
  },
  instructions: {
    backgroundColor: 'var(--gold-subtle)',
    borderRadius: 'var(--radius-lg)',
    padding: '1rem',
    fontSize: 'var(--text-sm)',
    color: 'var(--text)',
    textAlign: 'center',
    border: '1px solid var(--gold)',
  },
  error: {
    textAlign: 'center',
    padding: '3rem',
    color: 'var(--danger)',
  },
  errorIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 1rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
  },
  spinner: {
    width: '3rem',
    height: '3rem',
    border: '3px solid var(--border)',
    borderTopColor: 'var(--gold)',
    borderRadius: '50%',
    margin: '0 auto',
    animation: 'spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite',
  },
};

export default function VisitorQR() {
  const { qrCode, shortId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVisitInfo();
  }, [qrCode, shortId, token]);

  const fetchVisitInfo = async () => {
    // Handle both full and short URLs
    const visitId = shortId || qrCode;
    
    if (!visitId) {
      setError('Código QR inválido');
      setLoading(false);
      return;
    }

    try {
      // If it's a short URL, we need to find the full QR code first
      let endpoint;
      if (shortId) {
        endpoint = `/visits/qr/short/${shortId}`;
      } else {
        endpoint = `/visits/qr/${qrCode}${token ? `?token=${token}` : ''}`;
      }
      
      const response = await api.get(endpoint);
      // Asegurarnos de que la visita tenga el token para generar el QR
      const visitData = {
        ...response.data,
        qrToken: response.data.qrToken || token
      };
      setVisit(visitData);
    } catch (err) {
      console.error('Error fetching visit:', err);
      /* Cada caso dice lo suyo. Antes solo se traducían 404 y 403, y todo lo
         demás caía en «Error al cargar la información» — incluido un enlace
         mal copiado (400) y el freno por demasiadas recargas (429). Quien abre
         esto es alguien de fuera, en la calle: un mensaje que no distingue
         «este enlace está mal» de «inténtelo en un minuto» no le sirve. */
      const codigo = err.response?.status;
      if (codigo === 404) {
        setError('Esta visita no existe. Revise que el enlace esté completo.');
      } else if (codigo === 403) {
        setError('Código QR inválido o expirado');
      } else if (codigo === 400) {
        setError('El enlace está incompleto o mal copiado. Pida que se lo reenvíen.');
      } else if (codigo === 429) {
        setError('Demasiadas consultas seguidas. Espere un minuto y vuelva a intentarlo.');
      } else if (!err.response) {
        setError('No hay conexión. Revise su internet y vuelva a intentarlo.');
      } else {
        setError('No se pudo cargar el pase. Vuelva a intentarlo en un momento.');
      }
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>
            <div className="animate-spin" style={styles.spinner}></div>
            <p style={{ marginTop: '1rem' }}>Cargando información...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.error}>
            <AlertCircle style={styles.errorIcon} />
            <h2>{error}</h2>
            <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
              Por favor verifica el enlace o contacta al residente
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <VisitorQRView visit={visit} />
    </div>
  );
}